import { SetStateAction, useCallback, useEffect } from "react";
import { isEmpty, isEqual, omitBy, pickBy } from "lodash";
import { z } from "zod";

import {
  LOCKED_TRANSACTION_COLUMN,
  TRANSACTION_COLUMN_FIELDS,
} from "components/spend_data/columns";
import { createUseDebounce } from "lib/debounce";
import { useURLState } from "lib/hooks/useURLState";
import { useTracking } from "lib/tracking";
import { SpendSortOrder, SpendTransactionOrderBy } from "lib/types/graphQLEnums";
import { CountryRegionSchema } from "lib/types/models";
import {
  createChartTypeChangedEvent,
  createFilterChangedEvents,
  createGroupByChangedEvent,
  createIntervalChangedEvent,
  createViewModeChangedEvent,
} from "../tracking";

const useFilterDebounce = createUseDebounce(400);

/** What the user is looking at, in the `spendFilters` URL param. */
export const SpendDataFiltersSchema = z.object({
  buyerLists: z.array(z.string()).optional(),
  buyers: z.array(z.string()).optional(),
  supplierGuids: z.array(z.string()).optional(),
  dateRange: z
    .object({
      from: z.string().optional(),
      to: z.string().optional(),
      relativeFrom: z.string().optional(),
      relativeTo: z.string().optional(),
    })
    .optional(),
  buyerCategories: z.array(z.string()).optional(),
  buyerCountryRegions: CountryRegionSchema.optional(),
});
export type SpendDataFilters = z.infer<typeof SpendDataFiltersSchema>;

/** No filters at all: what the page holds before anything is set, and after "Clear all". */
export const EMPTY_SPEND_FILTERS: SpendDataFilters = {};

/**
 * The same thing as a set of form fields: every filter present at its empty value. The
 * values have to be defined rather than `undefined`, because react-hook-form's `reset`
 * leaves a controlled input showing its old value when the value it is given is undefined
 * - which is why the panel needs this rather than EMPTY_SPEND_FILTERS to empty itself.
 */
export const EMPTY_SPEND_FILTER_FIELDS: Required<SpendDataFilters> = {
  buyerLists: [],
  buyers: [],
  supplierGuids: [],
  dateRange: {},
  buyerCategories: [],
  buyerCountryRegions: { countries: [], regions: [], selected: [] },
};

export const SpendMeasureSchema = z.enum(["totalValue", "transactionCount"]);
export type SpendMeasure = z.infer<typeof SpendMeasureSchema>;

export const SpendChartTypeSchema = z.enum(["stackedArea", "stackedBar", "line"]);
export type SpendChartType = z.infer<typeof SpendChartTypeSchema>;

export const SpendTrendsGroupBySchema = z.enum(["time", "buyers", "suppliers", "buyerTypes"]);
export type SpendTrendsGroupBy = z.infer<typeof SpendTrendsGroupBySchema>;

export const SpendIntervalSchema = z.enum(["month", "quarter", "year"]);
export type SpendInterval = z.infer<typeof SpendIntervalSchema>;

/** How a transactions table is ordered. Each field falls back on its own, like the view's. */
export const TransactionSortSchema = z.object({
  orderBy: z.enum(SpendTransactionOrderBy).catch(SpendTransactionOrderBy.Date),
  sortOrder: z.enum(SpendSortOrder).catch(SpendSortOrder.Desc),
});

/**
 * How the user is looking at the data, in the `spendView` URL param. Each field falls back
 * on its own, so one unknown value in a shared link never wipes the rest of the view.
 */
export const SpendViewSchema = z.object({
  /** `table` lists the transactions themselves; Trends charts them over time. */
  mode: z.enum(["table", "trends"]).catch("trends"),
  // Trends only: what the chart is grouped by, what it plots, and over what buckets.
  trendsGroupBy: SpendTrendsGroupBySchema.catch("time"),
  measure: SpendMeasureSchema.catch("totalValue"),
  interval: SpendIntervalSchema.catch("quarter"),
  /**
   * The shape of a ranking chart. A stack leads because its height is the market total
   * and its bands are the ranking, so one chart answers both questions.
   */
  chartType: SpendChartTypeSchema.catch("stackedArea"),
  /** Order of the ranking under a Trends chart grouped by buyers, suppliers or buyer types. */
  sortOrder: z.enum(["ASC", "DESC"]).catch("DESC"),
  /** Order of the transactions table. */
  transactionSort: TransactionSortSchema.catch({
    orderBy: SpendTransactionOrderBy.Date,
    sortOrder: SpendSortOrder.Desc,
  }),
});
export type SpendView = z.infer<typeof SpendViewSchema>;

export const DEFAULT_SPEND_VIEW: SpendView = SpendViewSchema.parse({});

/**
 * Chosen columns for the transactions table. Absent means its defaults.
 *
 * Typed to the columns that exist and required to include the locked one, so a shared link
 * cannot ask for a table with no buyer in it or for a column the picker never offered.
 */
export const SpendColumnsSchema = z
  .array(z.enum(TRANSACTION_COLUMN_FIELDS))
  .min(1)
  .refine((fields) => fields.includes(LOCKED_TRANSACTION_COLUMN), {
    message: `columns must include ${LOCKED_TRANSACTION_COLUMN}`,
  });
export type SpendColumns = z.infer<typeof SpendColumnsSchema>;

// Hoisted so each schema keeps one identity: useURLState re-parses (and hands back a new
// object) whenever the schema it is given changes, which would make every render a change.
const OPTIONAL_FILTERS_SCHEMA = SpendDataFiltersSchema.optional();
const OPTIONAL_VIEW_SCHEMA = SpendViewSchema.optional();
const OPTIONAL_COLUMNS_SCHEMA = SpendColumnsSchema.optional();

/**
 * The filters that carry a value. The location picker empties to an object of empty lists,
 * which `isEmpty` counts as set, so a location is judged on its countries and regions.
 */
export function omitUnsetSpendFilters(filters: SpendDataFilters): SpendDataFilters {
  const set: SpendDataFilters = omitBy(filters, (value) =>
    typeof value === "boolean" ? !value : isEmpty(value),
  );
  if (!set.buyerCountryRegions?.countries?.length && !set.buyerCountryRegions?.regions?.length) {
    delete set.buyerCountryRegions;
  }
  return set;
}

/** Number of filters carrying a value, for the panel badge and the re-open button. */
export function countAppliedSpendFilters(filters: SpendDataFilters): number {
  return Object.keys(omitUnsetSpendFilters(filters)).length;
}

function stripEmptyFilters(filters: SpendDataFilters): SpendDataFilters {
  return pickBy(filters, (value) => value !== undefined) as SpendDataFilters;
}

/**
 * URL-backed state for the spend page: the filters (what), the view (how) and the table's
 * columns, as compressed params. Changing the view never touches the filters, and vice versa.
 *
 * The URL is the one source of filters. Edits from the filter panel go through
 * `debouncedSetFilters`, so a burst of changes lands once — but the URL, the page and the
 * queries still all read the same filters, rather than the page running ahead of the
 * queries for the length of the debounce. Deliberate changes (Clear all) go straight in.
 */
export function useSpendUrlState() {
  const { logEvents } = useTracking();

  const [urlFilters, setUrlFilters] = useURLState(
    "spendFilters",
    undefined,
    OPTIONAL_FILTERS_SCHEMA,
    { encoder: "compress" },
  );
  const [urlView, setUrlView] = useURLState("spendView", undefined, OPTIONAL_VIEW_SCHEMA, {
    encoder: "compress",
    replaceState: true,
  });
  const [urlColumns, setUrlColumns] = useURLState(
    "spendColumns",
    undefined,
    OPTIONAL_COLUMNS_SCHEMA,
    { encoder: "compress", replaceState: true },
  );

  const filters = urlFilters ?? EMPTY_SPEND_FILTERS;
  const view = urlView ?? DEFAULT_SPEND_VIEW;

  const setFilters = useCallback(
    (update: SetStateAction<SpendDataFilters>) => {
      const next = stripEmptyFilters(typeof update === "function" ? update(filters) : update);
      logEvents(...createFilterChangedEvents(filters, next));
      setUrlFilters(isEmpty(next) ? undefined : next);
    },
    [filters, logEvents, setUrlFilters],
  );

  // Cancelled on Clear all and on unmount, so a pending panel edit can't land afterwards.
  const debouncedSetFilters = useFilterDebounce(setFilters);
  useEffect(() => () => debouncedSetFilters.cancel(), [debouncedSetFilters]);

  const clearFilters = useCallback(() => {
    debouncedSetFilters.cancel();
    setFilters(EMPTY_SPEND_FILTERS);
  }, [debouncedSetFilters, setFilters]);

  /** Updates only the view fields given; back at the defaults, the param is dropped. */
  const setView = useCallback(
    (update: Partial<SpendView>) => {
      if (update.trendsGroupBy && update.trendsGroupBy !== view.trendsGroupBy) {
        logEvents(createGroupByChangedEvent(update.trendsGroupBy));
      }
      if (update.interval && update.interval !== view.interval) {
        logEvents(createIntervalChangedEvent(update.interval));
      }
      if (update.mode && update.mode !== view.mode) {
        logEvents(createViewModeChangedEvent(update.mode));
      }
      if (update.chartType && update.chartType !== view.chartType) {
        logEvents(createChartTypeChangedEvent(update.chartType));
      }
      const next = { ...view, ...update };
      setUrlView(isEqual(next, DEFAULT_SPEND_VIEW) ? undefined : next);
    },
    [logEvents, setUrlView, view],
  );

  /**
   * Writes a template's filters and view together. Untracked field by field: the caller
   * logs the template as one viewSelected event.
   */
  const applyTemplate = useCallback(
    (templateFilters: SpendDataFilters, templateView: Partial<SpendView>) => {
      // A panel edit still waiting on the debounce would otherwise land on top of the template.
      debouncedSetFilters.cancel();
      setUrlFilters(templateFilters);
      const next = { ...view, ...templateView };
      setUrlView(isEqual(next, DEFAULT_SPEND_VIEW) ? undefined : next);
    },
    [debouncedSetFilters, setUrlFilters, setUrlView, view],
  );

  /** Sets the transactions table's columns; passing the defaults clears the override. */
  const setColumns = useCallback(
    (fields: SpendColumns, defaultFields: SpendColumns) => {
      setUrlColumns(isEqual(fields, defaultFields) ? undefined : fields);
    },
    [setUrlColumns],
  );

  return {
    filters,
    appliedFiltersCount: countAppliedSpendFilters(filters),
    setFilters,
    debouncedSetFilters,
    clearFilters,
    view,
    setView,
    applyTemplate,
    columns: urlColumns,
    setColumns,
  };
}
